import moment from "moment";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { httpGetEnums } from "./enums";

import "moment/locale/fi";
import "moment/locale/sv";

import en from "./translations/en.json";
import fi from "./translations/fi.json";
import sv from "./translations/sv.json";

const LANG_CODES = ["fi", "en", "sv"];
const STORAGE_ROWS = "app_translations_rows";
const STORAGE_TS = "app_translations_timestamp";
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

const resources = {
  en: { translation: en },
  fi: { translation: fi },
  sv: { translation: sv },
};

i18n.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem("language") || "fi",
  fallbackLng: "fi",
  keySeparator: false,
  interpolation: {
    escapeValue: false,
  },
});

/**
 * Align moment.js with the same locale code as i18next (fi, en, sv).
 */
export function syncMomentLocale(code) {
  if (!code) return;
  moment.locale(code === "en" ? "en-gb" : code);
}

function resyncI18nLanguageFromStorage() {
  const lng = localStorage.getItem("language");
  if (lng === "fi" || lng === "en" || lng === "sv") {
    void i18n.changeLanguage(lng);
  }
}

/**
 * Merge DB strings into i18n. overwrite=false: do not replace keys already shipped in en.json/fi.json/sv.json.
 * Otherwise a bad or duplicate value_en in the DB overwrites English and the whole UI looks Finnish
 * even when i18n.language is "en".
 */
function applyAppTranslationRows(rows) {
  if (!rows || !Array.isArray(rows)) return;
  const bundles = {};
  LANG_CODES.forEach((lang) => {
    bundles[lang] = {};
  });
  rows.forEach((row) => {
    if (!row || !row.key) return;
    LANG_CODES.forEach((lang) => {
      const v = row[`value_${lang}`];
      if (v) bundles[lang][row.key] = v;
    });
  });
  LANG_CODES.forEach((lang) => {
    i18n.addResourceBundle(lang, "translation", bundles[lang], true, false);
  });
  resyncI18nLanguageFromStorage();
}

function loadCachedRowsIntoI18n() {
  const raw = localStorage.getItem(STORAGE_ROWS);
  if (!raw) return false;
  try {
    applyAppTranslationRows(JSON.parse(raw));
    return true;
  } catch {
    return false;
  }
}

function shouldRefreshFromApi() {
  const ts = localStorage.getItem(STORAGE_TS);
  if (!ts) return true;
  return Date.now() - parseInt(ts, 10) > SIX_HOURS_MS;
}

async function refreshTranslationsFromEnumsApi() {
  const response = await httpGetEnums();
  const rows =
    response?.data?.translations?.app_translation?.app_translations;
  if (!rows) return;
  localStorage.setItem(STORAGE_ROWS, JSON.stringify(rows));
  localStorage.setItem(STORAGE_TS, String(Date.now()));
  applyAppTranslationRows(rows);
}

loadCachedRowsIntoI18n();

if (shouldRefreshFromApi()) {
  refreshTranslationsFromEnumsApi().catch((err) => {
    console.error("Failed to load translations from enums API:", err);
  });
}

export default i18n;
